import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkArchiveExperiencesResponse,
  BulkUnarchiveExperiencesResponse,
  BulkUpdateExperienceTagsApplicationErrors,
  BulkUpdateExperienceTagsByFilterApplicationErrors,
  BulkUpdateExperienceTagsByFilterOptions,
  BulkUpdateExperienceTagsByFilterResponse,
  BulkUpdateExperienceTagsOptions,
  BulkUpdateExperienceTagsResponse,
  BulkUpdateExperiencesApplicationErrors,
  BulkUpdateExperiencesOptions,
  BulkUpdateExperiencesResponse,
  Experience,
  ExperienceCreatedEnvelope,
  ExperienceQuery,
  ExperienceSearch,
  ExperienceTagsModifiedEnvelope,
  ExperienceUpdatedEnvelope,
  ExperiencesQueryBuilder,
  GetExperienceBySlugResponse,
  MaskedExperience,
  QueryExperiencesResponse,
  SearchExperiencesResponse,
  UpdateExperience,
  UpdateExperienceApplicationErrors,
  bulkArchiveExperiences as universalBulkArchiveExperiences,
  bulkUnarchiveExperiences as universalBulkUnarchiveExperiences,
  bulkUpdateExperienceTags as universalBulkUpdateExperienceTags,
  bulkUpdateExperienceTagsByFilter as universalBulkUpdateExperienceTagsByFilter,
  bulkUpdateExperiences as universalBulkUpdateExperiences,
  createExperience as universalCreateExperience,
  getExperience as universalGetExperience,
  getExperienceBySlug as universalGetExperienceBySlug,
  queryExperiences as universalQueryExperiences,
  searchExperiences as universalSearchExperiences,
  typedQueryExperiences as universalTypedQueryExperiences,
  updateExperience as universalUpdateExperience,
} from './table-reservations-v1-experience-experiences.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/table-reservations' };

export function createExperience(
  httpClient: HttpClient
): CreateExperienceSignature {
  return (
    experience: NonNullablePaths<
      Experience,
      | `configuration`
      | `configuration.displayInfo`
      | `configuration.displayInfo.name`
      | `configuration.onlineReservations`
      | `configuration.onlineReservations.approval`
      | `configuration.onlineReservations.approval.mode`
      | `configuration.onlineReservations.maxGuests.number`
      | `configuration.onlineReservations.maximumReservationNotice.number`
      | `configuration.onlineReservations.maximumReservationNotice.unit`
      | `configuration.onlineReservations.minimumReservationNotice.number`
      | `configuration.onlineReservations.minimumReservationNotice.unit`
      | `configuration.onlineReservations.partySize`
      | `configuration.onlineReservations.partySize.max`
      | `configuration.onlineReservations.partySize.min`
      | `configuration.paymentPolicy`
      | `configuration.paymentPolicy.paymentPolicyType`
      | `reservationLocationId`,
      5
    >
  ) =>
    universalCreateExperience(
      experience,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateExperienceSignature {
  /**
   * Creates an experience.
   * @param - Experience to create.
   * @returns Created experience.
   */
  (
    experience: NonNullablePaths<
      Experience,
      | `configuration`
      | `configuration.displayInfo`
      | `configuration.displayInfo.name`
      | `configuration.onlineReservations`
      | `configuration.onlineReservations.approval`
      | `configuration.onlineReservations.approval.mode`
      | `configuration.onlineReservations.maxGuests.number`
      | `configuration.onlineReservations.maximumReservationNotice.number`
      | `configuration.onlineReservations.maximumReservationNotice.unit`
      | `configuration.onlineReservations.minimumReservationNotice.number`
      | `configuration.onlineReservations.minimumReservationNotice.unit`
      | `configuration.onlineReservations.partySize`
      | `configuration.onlineReservations.partySize.max`
      | `configuration.onlineReservations.partySize.min`
      | `configuration.paymentPolicy`
      | `configuration.paymentPolicy.paymentPolicyType`
      | `reservationLocationId`,
      5
    >
  ): Promise<
    NonNullablePaths<
      Experience,
      | `configuration.paymentPolicy.paymentPolicyType`
      | `configuration.onlineReservations.minimumReservationNotice.unit`
      | `configuration.onlineReservations.approval.mode`
      | `configuration.onlineReservations.businessSchedule.entries`
      | `configuration.onlineReservations.businessSchedule.entries.${number}.recurrence`
      | `configuration.reservationForm.customFieldDefinitions`
      | `tags.privateTags.tagIds`
      | `seoSettings.seoSchema.tags`
      | `seoSettings.seoSchema.tags.${number}.type`
      | `seoSettings.seoSchema.tags.${number}.children`
      | `seoSettings.seoSchema.tags.${number}.custom`
      | `seoSettings.seoSchema.tags.${number}.disabled`
      | `seoSettings.seoSchema.settings.preventAutoRedirect`
      | `seoSettings.seoSchema.settings.keywords`
      | `seoSettings.seoSchema.settings.keywords.${number}.term`
      | `seoSettings.seoSchema.settings.keywords.${number}.isMain`,
      7
    >
  >;
}

export function getExperience(httpClient: HttpClient): GetExperienceSignature {
  return (experienceId: string) =>
    universalGetExperience(
      experienceId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetExperienceSignature {
  /**
   * Retrieves an experience.
   * @param - ID of the experience to retrieve.
   * @returns Retrieved experience.
   */
  (experienceId: string): Promise<
    NonNullablePaths<
      Experience,
      | `configuration.paymentPolicy.paymentPolicyType`
      | `configuration.onlineReservations.minimumReservationNotice.unit`
      | `configuration.onlineReservations.approval.mode`
      | `configuration.onlineReservations.businessSchedule.entries`
      | `configuration.onlineReservations.businessSchedule.entries.${number}.recurrence`
      | `configuration.reservationForm.customFieldDefinitions`
      | `tags.privateTags.tagIds`
      | `seoSettings.seoSchema.tags`
      | `seoSettings.seoSchema.tags.${number}.type`
      | `seoSettings.seoSchema.tags.${number}.children`
      | `seoSettings.seoSchema.tags.${number}.custom`
      | `seoSettings.seoSchema.tags.${number}.disabled`
      | `seoSettings.seoSchema.settings.preventAutoRedirect`
      | `seoSettings.seoSchema.settings.keywords`
      | `seoSettings.seoSchema.settings.keywords.${number}.term`
      | `seoSettings.seoSchema.settings.keywords.${number}.isMain`,
      7
    >
  >;
}

export function getExperienceBySlug(
  httpClient: HttpClient
): GetExperienceBySlugSignature {
  return (slug: string) =>
    universalGetExperienceBySlug(
      slug,
      // @ts-ignore
      { httpClient }
    );
}

interface GetExperienceBySlugSignature {
  /**
   * Retrieves an experience by slug.
   *
   * The slug is a URL-friendly identifier for the experience page, derived from the experience display name in kebab-case format. For example, "wine-tasting-event".
   * @param - Slug of the experience to retrieve.
   */
  (slug: string): Promise<
    NonNullablePaths<
      GetExperienceBySlugResponse,
      | `experience.configuration.paymentPolicy.paymentPolicyType`
      | `experience.configuration.onlineReservations.minimumReservationNotice.unit`
      | `experience.configuration.onlineReservations.approval.mode`
      | `experience.configuration.onlineReservations.businessSchedule.entries`
      | `experience.configuration.onlineReservations.businessSchedule.entries.${number}.recurrence`
      | `experience.configuration.reservationForm.customFieldDefinitions`
      | `experience.tags.privateTags.tagIds`
      | `experience.seoSettings.seoSchema.tags`
      | `experience.seoSettings.seoSchema.tags.${number}.type`
      | `experience.seoSettings.seoSchema.tags.${number}.children`
      | `experience.seoSettings.seoSchema.tags.${number}.custom`
      | `experience.seoSettings.seoSchema.tags.${number}.disabled`
      | `experience.seoSettings.seoSchema.settings.preventAutoRedirect`
      | `experience.seoSettings.seoSchema.settings.keywords`
      | `experience.seoSettings.seoSchema.settings.keywords.${number}.term`
      | `experience.seoSettings.seoSchema.settings.keywords.${number}.isMain`,
      8
    >
  >;
}

export function updateExperience(
  httpClient: HttpClient
): UpdateExperienceSignature {
  return (
    _id: string,
    experience: NonNullablePaths<UpdateExperience, `revision`, 2>
  ) =>
    universalUpdateExperience(
      _id,
      experience,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExperienceSignature {
  /**
   * Updates an experience.
   *
   * Each time the experience is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the experience.
   * This ensures you're working with the latest experience information and prevents unintended overwrites.
   *
   * To update multiple experiences in a single request, call [Bulk Update Experiences](https://dev.wix.com/docs/api-reference/business-solutions/restaurants/reservations/experiences/bulk-update-experiences).
   * @param - Experience ID.
   * @returns Updated experience.
   */
  (
    _id: string,
    experience: NonNullablePaths<UpdateExperience, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      Experience,
      | `configuration.paymentPolicy.paymentPolicyType`
      | `configuration.onlineReservations.minimumReservationNotice.unit`
      | `configuration.onlineReservations.approval.mode`
      | `configuration.onlineReservations.businessSchedule.entries`
      | `configuration.onlineReservations.businessSchedule.entries.${number}.recurrence`
      | `configuration.reservationForm.customFieldDefinitions`
      | `tags.privateTags.tagIds`
      | `seoSettings.seoSchema.tags`
      | `seoSettings.seoSchema.tags.${number}.type`
      | `seoSettings.seoSchema.tags.${number}.children`
      | `seoSettings.seoSchema.tags.${number}.custom`
      | `seoSettings.seoSchema.tags.${number}.disabled`
      | `seoSettings.seoSchema.settings.preventAutoRedirect`
      | `seoSettings.seoSchema.settings.keywords`
      | `seoSettings.seoSchema.settings.keywords.${number}.term`
      | `seoSettings.seoSchema.settings.keywords.${number}.isMain`,
      7
    > & {
      __applicationErrorsType?: UpdateExperienceApplicationErrors;
    }
  >;
}

export function queryExperiences(
  httpClient: HttpClient
): QueryExperiencesSignature {
  return () =>
    universalQueryExperiences(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryExperiencesSignature {
  /**
   * Retrieves a list of experiences using the specified paging, filtering, and sorting.
   *
   * For a detailed list of supported operations, see the [Supported Filters and Sorting](https://dev.wix.com/docs/api-reference/business-solutions/restaurants/reservations/experiences/sorting-and-filtering) article.
   * To learn how to query reservation locations, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
   */
  (): ExperiencesQueryBuilder;
}

export function typedQueryExperiences(
  httpClient: HttpClient
): TypedQueryExperiencesSignature {
  return (query: ExperienceQuery) =>
    universalTypedQueryExperiences(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryExperiencesSignature {
  /** */
  (query: ExperienceQuery): Promise<
    NonNullablePaths<
      QueryExperiencesResponse,
      | `experiences`
      | `experiences.${number}.configuration.paymentPolicy.paymentPolicyType`,
      6
    >
  >;
}

export function searchExperiences(
  httpClient: HttpClient
): SearchExperiencesSignature {
  return (search: ExperienceSearch) =>
    universalSearchExperiences(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchExperiencesSignature {
  /**
   * Use this method to search the fields of the experiences on a site for a given expression.
   *
   * You can also use this method to perform data aggregations on a site's experience fields.
   * @param - Search query.
   */
  (search: ExperienceSearch): Promise<
    NonNullablePaths<
      SearchExperiencesResponse,
      | `experiences`
      | `experiences.${number}.configuration.paymentPolicy.paymentPolicyType`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function bulkUpdateExperiences(
  httpClient: HttpClient
): BulkUpdateExperiencesSignature {
  return (
    experiences: NonNullablePaths<
      MaskedExperience,
      `experience._id` | `experience.revision`,
      3
    >[],
    options?: BulkUpdateExperiencesOptions
  ) =>
    universalBulkUpdateExperiences(
      experiences,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateExperiencesSignature {
  /**
   * Updates up to 100 experiences in a single request.
   *
   * Each experience is updated independently. If an update fails for one experience, the failure is reported in that experience's `itemMetadata.error` and the remaining experiences are still processed. The call returns successfully even when some updates fail.
   *
   * Set `returnEntity` to `true` to receive the updated experiences in the response.
   *
   * Archived experiences can't be updated.
   *
   * To update a single experience, call [Update Experience](https://dev.wix.com/docs/api-reference/business-solutions/restaurants/reservations/experiences/update-experience).
   * @param - List of experiences to update, with the fields to update for each experience.
   */
  (
    experiences: NonNullablePaths<
      MaskedExperience,
      `experience._id` | `experience.revision`,
      3
    >[],
    options?: BulkUpdateExperiencesOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateExperiencesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateExperiencesApplicationErrors;
    }
  >;
}

export function bulkUpdateExperienceTags(
  httpClient: HttpClient
): BulkUpdateExperienceTagsSignature {
  return (experienceIds: string[], options?: BulkUpdateExperienceTagsOptions) =>
    universalBulkUpdateExperienceTags(
      experienceIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateExperienceTagsSignature {
  /**
   * Synchronously updates [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) on multiple experiences by experience IDs. A tag that appears in both assign and unassign lists is assigned.
   * @param - List of experience IDs to update tags for.
   */
  (experienceIds: string[], options?: BulkUpdateExperienceTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateExperienceTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateExperienceTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateExperienceTagsByFilter(
  httpClient: HttpClient
): BulkUpdateExperienceTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateExperienceTagsByFilterOptions
  ) =>
    universalBulkUpdateExperienceTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateExperienceTagsByFilterSignature {
  /**
   * Asynchronously updates [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) on multiple experiences using filter criteria. An empty filter updates all experiences. A tag that appears in both assign and unassign lists is assigned.
   * @param - Filter criteria to select experiences for tag updates.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateExperienceTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateExperienceTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateExperienceTagsByFilterApplicationErrors;
    }
  >;
}

export function bulkArchiveExperiences(
  httpClient: HttpClient
): BulkArchiveExperiencesSignature {
  return (experienceIds: string[]) =>
    universalBulkArchiveExperiences(
      experienceIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkArchiveExperiencesSignature {
  /**
   * Archives multiple experiences.
   *
   * Archiving an experience sets `configuration.visible` to `false`, preventing it from appearing on the live site and from accepting new reservations.
   *
   * To unarchive experiences, call [Bulk Unarchive Experiences](https://dev.wix.com/docs/api-reference/business-solutions/restaurants/reservations/experiences/bulk-unarchive-experiences).
   * @param - IDs of the experiences to archive.
   */
  (experienceIds: string[]): Promise<
    NonNullablePaths<
      BulkArchiveExperiencesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUnarchiveExperiences(
  httpClient: HttpClient
): BulkUnarchiveExperiencesSignature {
  return (experienceIds: string[]) =>
    universalBulkUnarchiveExperiences(
      experienceIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUnarchiveExperiencesSignature {
  /**
   * Unarchives multiple experiences.
   *
   * Unarchiving an experience only sets the archived status to `false`. It doesn't restore `configuration.visible` to its previous state.
   * To make the experience appear on the live site again, call [Update Experience](https://dev.wix.com/docs/api-reference/business-solutions/restaurants/reservations/experiences/update-experience) to set `configuration.visible` to `true`.
   *
   * To archive experiences, call [Bulk Archive Experiences](https://dev.wix.com/docs/api-reference/business-solutions/restaurants/reservations/experiences/bulk-archive-experiences).
   * @param - IDs of the experiences to unarchive.
   */
  (experienceIds: string[]): Promise<
    NonNullablePaths<
      BulkUnarchiveExperiencesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onExperienceCreated = EventDefinition(
  'wix.table_reservations.v1.experience_created',
  true,
  (event: ExperienceCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            {
              path: 'entity.configuration.displayInfo.richContent.metadata.createdTimestamp',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.metadata.updatedTimestamp',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'entity.experiencePageUrl' },
            { path: 'entity.configuration.reservationForm.privacyPolicy.url' },
            {
              path: 'entity.configuration.reservationForm.termsAndConditions.url',
            },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.configuration.displayInfo.coverImage' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.tocData.fontSize',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<ExperienceCreatedEnvelope>();
export const onExperienceTagsModified = EventDefinition(
  'wix.table_reservations.v1.experience_tags_modified',
  true,
  (event: ExperienceTagsModifiedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.experience.createdDate' },
            { path: 'data.experience.updatedDate' },
            {
              path: 'data.experience.configuration.displayInfo.richContent.metadata.createdTimestamp',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.metadata.updatedTimestamp',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'data.experience.experiencePageUrl' },
            {
              path: 'data.experience.configuration.reservationForm.privacyPolicy.url',
            },
            {
              path: 'data.experience.configuration.reservationForm.termsAndConditions.url',
            },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'data.experience.configuration.displayInfo.coverImage' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.tocData.fontSize',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.tocData.itemSpacing',
            },
            {
              path: 'data.experience.configuration.displayInfo.richContent.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<ExperienceTagsModifiedEnvelope>();
export const onExperienceUpdated = EventDefinition(
  'wix.table_reservations.v1.experience_updated',
  true,
  (event: ExperienceUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            {
              path: 'entity.configuration.displayInfo.richContent.metadata.createdTimestamp',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.metadata.updatedTimestamp',
            },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.metadata.createdTimestamp',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.metadata.updatedTimestamp',
            },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'entity.experiencePageUrl' },
            { path: 'entity.configuration.reservationForm.privacyPolicy.url' },
            {
              path: 'entity.configuration.reservationForm.termsAndConditions.url',
            },
            { path: 'modifiedFields.experiencePageUrl' },
            {
              path: 'modifiedFields.configuration.reservationForm.privacyPolicy.url',
            },
            {
              path: 'modifiedFields.configuration.reservationForm.termsAndConditions.url',
            },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.configuration.displayInfo.coverImage' },
            { path: 'modifiedFields.configuration.displayInfo.coverImage' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.tocData.fontSize',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.configuration.displayInfo.richContent.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.tocData.fontSize',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.tocData.itemSpacing',
            },
            {
              path: 'modifiedFields.configuration.displayInfo.richContent.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<ExperienceUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressHint,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  Alignment,
  AnchorData,
  App,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  ApplicationError,
  Approval,
  AspectRatio,
  AudioData,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Banner,
  BannerPosition,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulkActionMetadata,
  BulkArchiveExperiencesRequest,
  BulkArchiveExperiencesResponse,
  BulkArchiveExperiencesResult,
  BulkUnarchiveExperiencesRequest,
  BulkUnarchiveExperiencesResponse,
  BulkUnarchiveExperiencesResult,
  BulkUpdateExperienceTagsByFilterOptions,
  BulkUpdateExperienceTagsByFilterRequest,
  BulkUpdateExperienceTagsByFilterResponse,
  BulkUpdateExperienceTagsOptions,
  BulkUpdateExperienceTagsRequest,
  BulkUpdateExperienceTagsResponse,
  BulkUpdateExperienceTagsResult,
  BulkUpdateExperiencesOptions,
  BulkUpdateExperiencesRequest,
  BulkUpdateExperiencesResponse,
  BulkUpdateExperiencesResult,
  BulletedListData,
  BusinessSchedule,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  Categories,
  CellStyle,
  ChangeContext,
  ChangeContextPayloadOneOf,
  CheckboxListData,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  ColumnSize,
  Configuration,
  ConsentPolicy,
  CreateExperienceRequest,
  CreateExperienceResponse,
  Crop,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  CustomFieldDefinition,
  CustomTag,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  DayOfWeek,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  Design,
  DesignTarget,
  Dimensions,
  Direction,
  DisplayInfo,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  EmailMarketingCheckbox,
  EmbedData,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Entry,
  EntryRecurrenceOptionsOneOf,
  EventData,
  EventMetadata,
  Experience,
  ExperienceCreatedEnvelope,
  ExperienceQuerySpec,
  ExperienceSearchSpec,
  ExperienceTagsModifiedEnvelope,
  ExperienceUpdatedEnvelope,
  ExperiencesQueryBuilder,
  ExperiencesQueryResult,
  ExtendedFields,
  File,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FontFamilyData,
  FontSizeData,
  FontType,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GeoCoordinates,
  GetExperienceBySlugRequest,
  GetExperienceBySlugResponse,
  GetExperienceRequest,
  GetExperienceResponse,
  Gradient,
  GradientType,
  GroupByValueResults,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataStyles,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  IncludeMissingValuesOptions,
  Indentation,
  InitialExpandedItems,
  Interval,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  Item,
  ItemDataOneOf,
  ItemMetadata,
  ItemStyle,
  Keyword,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutType,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListItemNodeData,
  ListStyle,
  ListValue,
  Locale,
  MapData,
  MapSettings,
  MapType,
  MaskedExperience,
  MaxGuests,
  Media,
  MentionData,
  MessageEnvelope,
  Metadata,
  MissingValues,
  Mode,
  Multilingual,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NoticePeriod,
  NullValue,
  Oembed,
  OnOptions,
  OnOptionsOptionsOneOf,
  OneTimeOptions,
  OnlineReservations,
  OnlineReservationsBusinessSchedule,
  Option,
  OptionDesign,
  OptionLayout,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  Page,
  Pages,
  ParagraphData,
  PartySize,
  PaymentPolicy,
  PaymentPolicyOptionsOneOf,
  PaymentPolicyType,
  PerGuestOptions,
  Permissions,
  Placement,
  PlacementType,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  PollSettings,
  Position,
  PricingData,
  PrivacyPolicy,
  PrivacyPolicyValueOneOf,
  Properties,
  PropertiesChange,
  QueryExperiencesRequest,
  QueryExperiencesResponse,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  Recurrence,
  Rel,
  ReservationForm,
  Resizing,
  ResolutionMethod,
  ResponsivenessBehaviour,
  RestoreInfo,
  Results,
  RibbonStyles,
  RichContent,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  Scaling,
  SearchDetails,
  SearchDetailsMode,
  SearchExperiencesRequest,
  SearchExperiencesResponse,
  SeoSchema,
  SeoSettings,
  Settings,
  ShapeData,
  ShapeDataStyles,
  SiteCloned,
  SiteCreated,
  SitePropertiesEvent,
  SitePropertiesNotification,
  SketchData,
  SmartBlockCellData,
  SmartBlockData,
  SmartBlockDataType,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  Source,
  SpecialHourPeriod,
  SpecificTablesOptions,
  Spoiler,
  SpoilerData,
  StartDaysAndTimes,
  StartDaysAndTimesDayOfWeek,
  Stop,
  Styles,
  StylesBorder,
  StylesPosition,
  SupportedLanguage,
  Table,
  TableAssignmentType,
  TableAvailabilityType,
  TableCellData,
  TableCombination,
  TableData,
  TableManagement,
  TableManagementMode,
  TableManagementOptionsOneOf,
  Tag,
  TagList,
  Tags,
  TagsModified,
  Target,
  TermsAndConditions,
  TermsAndConditionsValueOneOf,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  TimePeriod,
  TocData,
  Translation,
  Type,
  URI,
  URIs,
  Unit,
  UpdateExperience,
  UpdateExperienceRequest,
  UpdateExperienceResponse,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  Variant,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  ViewMode,
  ViewRole,
  VoteRole,
  WebhookIdentityType,
  WeeklyOptions,
  Width,
  WidthType,
  utils,
} from './table-reservations-v1-experience-experiences.universal.js';
